import type { DocumentWithMetadataQuery } from "generated-graphql/graphql";

import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import type { SuggestedFacility } from "../types";

import { DOCUMENT_WITH_METADATA, FACILITY_MATCHES_FOR_DOCUMENTS } from "../queries";

type UseDocumentMetadataParams = {
  documentId: string | undefined;
  open: boolean;
  hasDocumentAI: boolean;
};

type DocumentWithMetadataResult = DocumentWithMetadataQuery["document"];
type DocumentMetadataResult = NonNullable<DocumentWithMetadataResult>["documentMetadata"];

type UseDocumentMetadataResult = {
  document?: DocumentWithMetadataResult;
  metadata?: DocumentMetadataResult;
  loading: boolean;
  suggestedFacilities: SuggestedFacility[];
  suggestedFacilitiesLoading: boolean;
  previewUrl: string;
};

export function useDocumentMetadata({
  documentId,
  open,
  hasDocumentAI,
}: UseDocumentMetadataParams): UseDocumentMetadataResult {
  const { data, loading } = useQuery(DOCUMENT_WITH_METADATA, {
    variables: { id: documentId ?? "" },
    skip: !open || !documentId,
    fetchPolicy: "cache-and-network",
  });

  const { data: facilityMatchesData, loading: facilityMatchesLoading } = useQuery(
    FACILITY_MATCHES_FOR_DOCUMENTS,
    {
      variables: {
        documentIds: documentId ? [documentId] : [],
        limit: 3,
      },
      skip: !open || !documentId || !hasDocumentAI,
      fetchPolicy: "cache-first",
    }
  );

  const suggestedFacilities: SuggestedFacility[] = useMemo(() => {
    const matches = facilityMatchesData?.facilityMatchesForDocuments?.[0]?.matches ?? [];

    return matches
      .map((match) => match.facility)
      .filter((facility): facility is NonNullable<typeof facility> => !!facility)
      .map((facility) => ({
        id: facility.id,
        name: facility.name ?? "",
        customerFacilityId: facility.customerFacilityId ?? null,
        streetAddress1: facility.streetAddress1 ?? null,
        streetAddress2: facility.streetAddress2 ?? null,
        city: facility.city ?? null,
        state: facility.state ?? null,
        zip: facility.zip ?? null,
      }));
  }, [facilityMatchesData]);

  const document = useMemo(() => data?.document, [data]);
  const metadata = useMemo(() => document?.documentMetadata, [document]);
  const previewUrl = useMemo(() => document?.downloadLink ?? "", [document]);

  return {
    document,
    metadata,
    loading,
    suggestedFacilities,
    suggestedFacilitiesLoading: facilityMatchesLoading,
    previewUrl,
  };
}
